/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	Lock,
	Database,
	Share2,
	ExternalLink,
	UserCheck,
	RefreshCw,
	Mail,
	ShieldQuestion,
	MessageSquare,
	FileText,
	Info,
	CheckCircle,
	ShieldCheck,
} from "lucide-react"; // Refined Icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path
import ReactMarkdown from "react-markdown";

// --- Structured Privacy Policy Data ---
// (Keep legal text accurate, structure for readability)
const policyData = {
	lastUpdated: "October 27, 2023", // Add Last Updated Date
	introduction: {
		title: "Your Privacy Matters",
		icon: Lock,
		text: [
			"Off Cut Shades is committed to protecting your privacy. This policy explains what information we collect, how we use and share it, and the choices you have regarding your data when you use our website and services.",
			"We encourage you to read this policy carefully. By using our site, you agree to the collection and use of information in accordance with this policy.",
		],
	},
	sections: [
		{
			id: "collection",
			icon: Database,
			title: "Information We Collect",
			content: [
				{
					subtitle: "Information You Provide Directly",
					text: "When you create an account, place an order, contact support, or subscribe to our newsletter, we collect information such as your:",
					list: [
						"Name",
						"Email Address",
						"Phone Number",
						"Shipping/Billing Address",
						"Payment Information (processed securely by our partners, not stored directly by us)",
					],
				},
				{ separator: true }, // Visual separator
				{
					subtitle: "Information Collected Automatically",
					text: "Like most websites, we automatically collect certain information when you visit:",
					list: [
						"**Device Information:** IP address, browser type, operating system, device identifiers.",
						"**Usage Information:** Pages visited, time spent on pages, links clicked, referring website.",
						"**Cookies & Similar Technologies:** We use cookies (small data files stored on your device) and similar technologies like web beacons to help our site function, analyze usage, and personalize your experience. You can manage cookie preferences through your browser settings.",
					],
				},
				{ separator: true },
				{
					subtitle: "Information From Other Sources",
					text: "We may occasionally receive information about you from other sources, such as public databases or third-party partners (e.g., for fraud prevention), and combine it with information we collect directly.",
				},
			],
		},
		{
			id: "use",
			icon: CheckCircle, // Example icon
			title: "How We Use Your Information",
			content: [
				{
					text: "We use the information we collect for various purposes, including to:",
					list: [
						"Process and fulfill your orders (including shipping and payment).",
						"Create and manage your account.",
						"Communicate with you about your orders, requests, and updates.",
						"Provide customer support.",
						"Improve and personalize our website and services.",
						"Analyze website usage and trends.",
						"Send marketing communications (if you opt-in), such as newsletters and special offers.",
						"Prevent fraud and ensure security.",
						"Comply with legal obligations.",
					],
				},
			],
		},
		{
			id: "sharing",
			icon: Share2,
			title: "How We Share Your Information",
			content: [
				{
					text: "We value your trust and share your information only in limited circumstances:",
					list: [
						"**Service Providers:** With trusted third-party companies who perform services on our behalf (e.g., payment processors like Stripe/PayPal, shipping carriers like Canada Post/Canpar, email service providers like Mailchimp). They only have access to information needed to perform their functions and are obligated to protect it.",
						"**Business Transfers:** In connection with a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction.",
						"**Legal Requirements:** If required by law, subpoena, or other legal process, or if we believe in good faith that disclosure is necessary to protect our rights, your safety, or the safety of others, investigate fraud, or respond to a government request.",
						"**With Your Consent:** We may share information for other purposes with your explicit consent.",
					],
					text2: "We do not sell your personal information to third parties.",
				},
			],
		},
		// Add sections for 'Your Rights / Choices', 'Data Security', 'Data Retention' if applicable/required
		{
			id: "security",
			icon: ShieldCheck, // Use specific security icon
			title: "Data Security",
			content: [
				{
					text: "We implement reasonable administrative, technical, and physical security measures to protect your information from unauthorized access, use, or disclosure. We use secure connections (HTTPS) for data transmission. However, no internet transmission is 100% secure, and we cannot guarantee absolute security.",
				},
			],
		},
		{
			id: "links",
			icon: ExternalLink,
			title: "Third-Party Links",
			content: [
				{
					text: "Our website may contain links to other sites not operated by us. If you click a third-party link, you will be directed to their site. We have no control over and assume no responsibility for the content or privacy practices of any third-party sites or services. We strongly advise you to review the privacy policy of every site you visit.",
				},
			],
		},
		{
			id: "age",
			icon: UserCheck,
			title: "Age of Consent",
			content: [
				{
					text: "Our services are not directed to individuals under the age of majority in their jurisdiction. By using our site, you represent that you are at least the age of majority in your state or province of residence, or that you are the age of majority and have given us consent to allow any of your minor dependents to use this site.",
				},
			],
		},
		{
			id: "changes",
			icon: RefreshCw,
			title: "Changes to This Policy",
			content: [
				{
					text: `We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.`,
				},
			],
		},
		{
			id: "contact",
			icon: Mail,
			title: "Contact Us About Privacy",
			content: [
				{
					text: (
						<>
							If you have any questions or concerns about this Privacy Policy or
							our data practices, please contact us at{" "}
							<a
								href="mailto:info@offcutshades.com"
								className="text-primary hover:underline font-medium"
							>
								info@offcutshades.com
							</a>{" "}
							or use the general contact methods available on our{" "}
							<Link
								to="/contact"
								className="text-primary hover:underline font-medium"
							>
								Contact Us
							</Link>{" "}
							page.
						</>
					),
				}, // Use a dedicated privacy email if possible
			],
		},
	],
};

const PrivacyPolicy = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			{" "}
			{/* Standard background */}
			<Helmet>
				<title>Privacy Policy | Off Cut Shades</title>
				<meta
					name="description"
					content="Read the Off Cut Shades privacy policy to understand how we collect, use, and protect your personal information when you use our website and services."
				/>{" "}
				{/* Updated Desc */}
				<link
					rel="canonical"
					href="https://offcutshades.com/help/privacy-policy"
				/>
				<meta name="robots" content="noindex, follow" />{" "}
				{/* Usually okay to allow following links */}
			</Helmet>
			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<Lock size={36} strokeWidth={1.5} /> {/* Main Icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Privacy Policy
					</motion.h1>
					{policyData.lastUpdated && (
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.3 }}
							className="text-sm text-gray-500"
						>
							Last Updated: {policyData.lastUpdated}
						</motion.p>
					)}
				</section>
				{/* --- Introduction Section --- */}
				<section className="container mx-auto px-4 max-w-3xl mx-auto mb-10 md:mb-12">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.5 }}
						className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 space-y-4 text-base text-gray-700"
					>
						{policyData.introduction.text.map((paragraph, idx) => (
							<p key={`intro-${idx}`}>{paragraph}</p>
						))}
					</motion.div>
				</section>
				{/* --- Policy Details Sections --- */}
				<section className="container mx-auto px-4 max-w-3xl mx-auto">
					<div className="space-y-8">
						{policyData.sections.map((section, index) => (
							<motion.div
								key={section.id}
								className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" // Reduced shadow slightly
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, amount: 0.2 }} // Trigger slightly earlier
								transition={{ duration: 0.5, delay: index * 0.05 }}
							>
								{/* Section Header */}
								<div className="p-5 md:p-6 border-b border-gray-100">
									<div className="flex items-center gap-3">
										<div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
											{React.createElement(section.icon, {
												size: 20,
												strokeWidth: 2,
											})}
										</div>
										<h2 className="text-lg md:text-xl font-semibold text-gray-800">
											{section.title}
										</h2>
									</div>
								</div>
								{/* Section Content */}
								<div className="p-5 md:p-6 space-y-4 text-sm text-gray-700">
									{section.content.map((contentItem, cIndex) => (
										<div key={`${section.id}-${cIndex}`}>
											{/* Visual separator */}
											{contentItem.separator && (
												<hr className="my-4 border-gray-100" />
											)}
											{/* Subtitle */}
											{contentItem.subtitle && (
												<h3 className="text-base font-semibold text-gray-800 mb-2">
													{contentItem.subtitle}
												</h3>
											)}
											{/* Main Text */}
											{contentItem.text && (
												// Render text, allowing JSX for links
												<p className="mb-2 last:mb-0 leading-relaxed">
													{
														typeof contentItem.text === "string" ? (
															<span
																dangerouslySetInnerHTML={{
																	__html: contentItem.text.replace(
																		/\*\*(.*?)\*\*/g,
																		"<strong>$1</strong>"
																	),
																}}
															/>
														) : (
															contentItem.text
														) // Render JSX directly
													}
												</p>
											)}
											{/* Bullet List */}
											{contentItem.list && (
												<ul className="list-disc list-outside space-y-1.5 pl-5 mt-2">
													{contentItem.list.map((item, itemIndex) => (
														<li key={itemIndex}>
															<ReactMarkdown>{item}</ReactMarkdown>
														</li>
													))}
												</ul>
											)}
											{/* Secondary Text */}
											{contentItem.text2 && (
												<p className="mt-3 font-medium text-gray-800">
													{contentItem.text2}
												</p>
											)}
										</div>
									))}
								</div>
							</motion.div>
						))}
					</div>
				</section>
				<ContactBlock
					title="Privacy Questions?"
					description="If you have questions about this policy or how we handle your data, please don't hesitate to reach out."
				/>
			</main>
		</div>
	);
};

export default PrivacyPolicy;
